import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ClipboardText, Warning, Clock, CheckCircle, Plus } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import NewTaskDialog from "@/components/NewTaskDialog";

/**
 * "My Tasks" dashboard widget — 4 counters, all clickable, each drilling into
 * the Tasks page pre-filtered to the caller's own workload.
 *
 * Owns the Quick Add Task modal (opened via the "+ New Task" header button).
 * On successful create: closes the modal, toasts, reloads its own counts, and
 * fires `onTaskCreated()` so the parent (Dashboard) can refresh sibling
 * widgets — e.g. the Department Queues tiles when the new task lands in a
 * manager-visible queue.
 *
 * DSG-001 compliant — semantic tokens only.
 */
export default function MyTasksWidget({ onTaskCreated }) {
  const nav = useNavigate();
  const { user } = useAuth();
  const canCreate = user && ["Administrator", "Owner", "Office Staff"].includes(user.role);
  const [counts, setCounts] = useState({
    open: 0, in_progress: 0, due_today: 0, overdue: 0, completed_recent: 0,
  });
  const [loading, setLoading] = useState(true);
  const [newOpen, setNewOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/tasks/counts`);
      setCounts(r.data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 60_000);
    return () => clearInterval(t);
  }, [load]);

  const openTotal = (counts.open || 0) + (counts.in_progress || 0);

  const CARDS = [
    { k: "open",      label: "Open Tasks",     value: openTotal,               icon: ClipboardText, tone: "blue",    section: "open" },
    { k: "due_today", label: "Due Today",      value: counts.due_today,        icon: Clock,         tone: "amber",   section: "due_today" },
    { k: "overdue",   label: "Overdue",        value: counts.overdue,          icon: Warning,       tone: "red",     section: "overdue" },
    { k: "completed", label: "Recently Done",  value: counts.completed_recent, icon: CheckCircle,   tone: "emerald", section: "completed" },
  ];

  const TONE = {
    blue:    "border-status-info-bg bg-status-info-bg text-status-info-fg",
    amber:   "border-status-warning-bg bg-status-warning-bg text-status-warning-fg",
    red:     "border-status-error-bg bg-status-error-bg text-status-error-fg",
    emerald: "border-status-success-bg bg-status-success-bg text-status-success-fg",
  };

  return (
    <>
      <div data-testid="my-tasks-widget" className="rounded-md border border-divider bg-surface">
        <div className="flex items-center justify-between border-b border-divider px-4 py-2.5">
          <h3 className="text-sm font-semibold uppercase tracking-wider text-ink-secondary">My Tasks</h3>
          <div className="flex items-center gap-3">
            {canCreate && (
              <button
                data-testid="my-tasks-new-task"
                onClick={() => setNewOpen(true)}
                className="inline-flex items-center gap-1 rounded-md bg-brand px-2.5 py-1 text-xs font-semibold text-brand-foreground shadow-token-sm hover:bg-brand-hover"
              >
                <Plus size={12} weight="bold" /> New Task
              </button>
            )}
            <button
              data-testid="my-tasks-open-page"
              onClick={() => nav("/tasks")}
              className="text-xs font-medium text-ink-secondary hover:text-brand hover:underline"
            >
              View all →
            </button>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3 p-4 sm:grid-cols-4">
          {CARDS.map((c) => {
            const Icon = c.icon;
            return (
              <button
                key={c.k}
                data-testid={`my-tasks-card-${c.k}`}
                onClick={() => nav(`/tasks?section=${c.section}`)}
                className={`flex flex-col items-start gap-1 rounded-md border p-3 text-left transition hover:brightness-95 ${TONE[c.tone]}`}
              >
                <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider">
                  <Icon size={13} weight="bold" /> {c.label}
                </div>
                <div className="text-3xl font-bold leading-none">
                  {loading ? "—" : c.value}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {newOpen && (
        <NewTaskDialog
          onClose={() => setNewOpen(false)}
          onCreated={() => {
            setNewOpen(false);
            toast.success("Task created");
            load();
            onTaskCreated?.();
          }}
        />
      )}
    </>
  );
}
